import gtcrnWasmUrl from '@sapphi-red/web-noise-suppressor/gtcrn.wasm?url'
import gtcrnWorkletUrl from '@sapphi-red/web-noise-suppressor/gtcrnWorklet.js?url'
import rnnoiseSimdWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url'
import rnnoiseWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise.wasm?url'
import rnnoiseWorkletUrl from '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url'

import type { AudioProcessorOptions, Track, TrackProcessor } from 'livekit-client'

import { NoiseSuppressionModel } from '@ts-classroom/enums'

import {
  NOISE_SUPPRESSION_CHANNELS,
  NOISE_SUPPRESSION_MODEL,
  NOISE_SUPPRESSION_PROCESSOR_NAME,
  NOISE_SUPPRESSION_SAMPLE_RATE,
} from '@config/classroom-noise-suppression'

type NoiseSuppressionProcessor = TrackProcessor<Track.Kind.Audio, AudioProcessorOptions>
type NoiseSuppressionLibrary = typeof import('@sapphi-red/web-noise-suppressor')

interface NoiseSuppressionNode extends AudioNode {
  destroy: () => void
}

interface ModelAssets {
  workletUrl: string
  loadWasm: (library: NoiseSuppressionLibrary) => Promise<ArrayBuffer>
  createNode: (library: NoiseSuppressionLibrary, context: AudioContext, wasmBinary: ArrayBuffer) => NoiseSuppressionNode
}

interface LoadedModel {
  library: NoiseSuppressionLibrary
  wasmBinary: ArrayBuffer
}

interface NoiseSuppressionGraph {
  source: MediaStreamAudioSourceNode
  node: NoiseSuppressionNode
  destination: MediaStreamAudioDestinationNode
}

const MODEL_ASSETS: Record<NoiseSuppressionModel, ModelAssets> = {
  [NoiseSuppressionModel.Rnnoise]: {
    workletUrl: rnnoiseWorkletUrl,
    loadWasm: (library) => library.loadRnnoise({ url: rnnoiseWasmUrl, simdUrl: rnnoiseSimdWasmUrl }),
    createNode: (library, context, wasmBinary) =>
      new library.RnnoiseWorkletNode(context, { maxChannels: NOISE_SUPPRESSION_CHANNELS, wasmBinary }),
  },
  [NoiseSuppressionModel.Gtcrn]: {
    workletUrl: gtcrnWorkletUrl,
    loadWasm: (library) => library.loadGtcrn({ url: gtcrnWasmUrl }),
    createNode: (library, context, wasmBinary) =>
      new library.GtcrnWorkletNode(context, { maxChannels: NOISE_SUPPRESSION_CHANNELS, wasmBinary }),
  },
}

let modelPromise: Promise<LoadedModel> | null = null

const loadModel = (assets: ModelAssets): Promise<LoadedModel> => {
  modelPromise ??= import('@sapphi-red/web-noise-suppressor')
    .then(async (library) => ({ library, wasmBinary: await assets.loadWasm(library) }))
    .catch((error: unknown) => {
      modelPromise = null
      throw error
    })

  return modelPromise
}

const buildGraph = (
  context: AudioContext,
  assets: ModelAssets,
  model: LoadedModel,
  track: MediaStreamTrack,
): NoiseSuppressionGraph => {
  const source = context.createMediaStreamSource(new MediaStream([track]))
  const node = assets.createNode(model.library, context, model.wasmBinary)
  const destination = context.createMediaStreamDestination()

  source.connect(node)
  node.connect(destination)

  return { source, node, destination }
}

const tearDownGraph = (graph: NoiseSuppressionGraph): void => {
  graph.source.disconnect()
  graph.node.disconnect()
  graph.node.destroy()
  graph.destination.stream.getAudioTracks().forEach((track) => track.stop())
}

export const preloadNoiseSuppression = async (): Promise<void> => {
  await loadModel(MODEL_ASSETS[NOISE_SUPPRESSION_MODEL]).then(() => undefined, () => undefined)
}

export const createNoiseSuppressionProcessor = (): NoiseSuppressionProcessor => {
  const assets = MODEL_ASSETS[NOISE_SUPPRESSION_MODEL]

  let context: AudioContext | null = null
  let graph: NoiseSuppressionGraph | null = null

  const processor: NoiseSuppressionProcessor = {
    name: NOISE_SUPPRESSION_PROCESSOR_NAME,
    processedTrack: undefined,

    async init ({ track }: AudioProcessorOptions): Promise<void> {
      await processor.destroy()

      const ownContext = new AudioContext({ sampleRate: NOISE_SUPPRESSION_SAMPLE_RATE })
      context = ownContext

      try {
        if (!ownContext.audioWorklet) throw new Error('AudioWorklet is not available')

        const [model] = await Promise.all([
          loadModel(assets),
          ownContext.audioWorklet.addModule(assets.workletUrl),
        ])
        await ownContext.resume()
        if (ownContext.state !== 'running') throw new Error(`AudioContext is ${ownContext.state}`)

        graph = buildGraph(ownContext, assets, model, track)
        processor.processedTrack = graph.destination.stream.getAudioTracks()[0]
      } catch (error) {
        console.warn('[classroom] noise suppression unavailable, publishing the raw microphone', error)
        await processor.destroy()
      }
    },

    async restart ({ track }: AudioProcessorOptions): Promise<void> {
      if (!context || !graph) return

      tearDownGraph(graph)
      graph = null
      processor.processedTrack = undefined

      try {
        graph = buildGraph(context, assets, await loadModel(assets), track)
        processor.processedTrack = graph.destination.stream.getAudioTracks()[0]
      } catch (error) {
        console.warn('[classroom] noise suppression unavailable after a device switch, publishing the raw microphone', error)
        await processor.destroy()
      }
    },

    async destroy (): Promise<void> {
      if (graph) tearDownGraph(graph)
      graph = null
      processor.processedTrack = undefined

      const ownContext = context
      context = null
      if (ownContext && ownContext.state !== 'closed') await ownContext.close()
    },
  }

  return processor
}
